import React, { useEffect, useRef, useState } from "react";
import FormContainer from "../components/FormContainer";
import Map from "../components/Map";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getClinicDetails } from "../actions/clinicActions";
import Skeleton from "react-loading-skeleton";
import ReactToPrint from "react-to-print";
import Loader from "../components/Loader";
import Message from "../components/Message";
import specialty from "../data/specialty";
import {
  Col,
  Form,
  ListGroup,
  ListGroupItem,
  Row,
  FormGroup,
  Button,
  ProgressBar,
} from "react-bootstrap";
import { getVisitDetails, listClinicVisits } from "../actions/visitActions";
import ScrollUp from "../components/ScrollUp";
import Meta from "../components/Meta";
import { endOfDay, format, parseISO, startOfDay } from "date-fns";
import { CSVLink } from "react-csv";
import {
  FaArrowAltCircleDown,
  FaDownload,
  FaExternalLinkAlt,
  FaFileExcel,
  FaHome,
  FaPrint,
} from "react-icons/fa";
import Help from "../components/Help";
import { VisitListPrint } from "../components/VisitListPrint";
import { notify } from "../components/notify";

const VisitListScreen = () => {
  const params = useParams();
  const clinicId = params.clinicId;

  const searchOptions = [
    { idx: "0", title: "Search By Date" },
    { idx: "1", title: "Search By Interval" },
  ];

  const [searchTitleId, setSearchTitleId] = useState("0");

  const currentDate = new Date();

  const [reqDate, setReqDate] = useState(format(currentDate, "yyyy-MM-dd"));

  const [startDate, setStartDate] = useState(
    startOfDay(currentDate).toISOString(),
  );

  const [endDate, setEndDate] = useState(endOfDay(currentDate).toISOString());

  const [detailedVisits, setDetailedVisits] = useState([]);
  const [errorVisit, setErrorVisit] = useState("");
  const [progression, setProgression] = useState();

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const clinicDetails = useSelector((state) => state.clinicDetails);
  const { norequest, loading, clinic, error: clinicError } = clinicDetails;

  const visitsOfClinicStore = useSelector((state) => state.visitsOfClinicStore);
  const {
    norequest: visitsNoReq,
    loading: loadingVisits,
    clinicVisits,
    csv,
    error: errorVisits,
  } = visitsOfClinicStore;

  const handleSearchTitleId = (e) => {
    setSearchTitleId(e.target.value);
  };
  const isAdmin =
    clinic?.members
      .find((m) => m.memberId.toString() === userInfo?._id.toString())
      ?.permissionId.toString() === "0"
      ? true
      : false;
  console.log(
    clinic?.members
      .find((m) => m.memberId.toString() === userInfo?._id.toString())
      ?.permissionId.toString(),
  );

  const handleBackup = async () => {
    if (!isAdmin) {
      notify({
        title: "Backup Warning",
        message: (
          <p className="lead">You have NO permission to do such action.</p>
        ),
        type: "info",
        duration: 3000,
      });
      return;
    }
    try {
      const results = [];

      // Fetch all visits with details
      for (let i = 0; i < clinicVisits.length; i++) {
        setProgression(((i + 1) * 100) / clinicVisits.length);

        const visitData = await getVisitDetails({
          visitId: clinicVisits[i]._id,
          userInfo,
          dispatch,
          setErrorVisit,
        });

        if (visitData) results.push(visitData);
      }

      // Group by unique patients
      const uniquePatients = Object.values(
        results.reduce((acc, visit) => {
          const pid = visit.patientId;

          if (!acc[pid]) {
            const matchingVisit = clinicVisits.find(
              (v) => v.patientDetails._id.toString() === pid.toString(),
            );

            if (!matchingVisit) {
              // guard against a visit whose patient isn't in clinicVisits
              return acc;
            }

            acc[pid] = {
              patientId: pid,
              patientDetails: matchingVisit.patientDetails,
              visits: [],
            };
          }

          const matchedSpecialty = specialty.find(
            (s) => s.id === visit.specialtyId.toString(),
          );
          acc[pid].visits.push({
            visitId: visit._id,
            visitDate: visit.visitDate,
            isLocked: visit.isLocked,
            clinicName: visit.clinicName,
            specialtyId: visit.specialtyId,
            specialtyLabel: matchedSpecialty?.label ?? null,
            secretarySection: visit.secretarySection ?? null,
            nurseSection: visit.nurseSection ?? null,
            physicianSection: visit.physicianSection ?? null,
          });

          return acc;
        }, {}),
      );

      // Save final unique patients with full visits
      setDetailedVisits(uniquePatients);
    } catch (err) {
      setErrorVisit(err.message || "Failed to backup visits.");
    } finally {
      setProgression(100);
    }
  };

  const componentRef = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    if (!userInfo) {
      navigate(`/login-screen?returnFrom=${location.pathname}`);
    } else {
      if (norequest || !clinic || clinic._id !== clinicId) {
        dispatch(getClinicDetails(clinicId));
      } else {
        if (!visitsNoReq) {
          dispatch(
            listClinicVisits({
              clinicId,
              startDate,
              endDate,
            }),
          );
        }
      }
    }
  }, [
    clinicId,
    norequest,
    clinic,
    userInfo,
    navigate,
    dispatch,
    reqDate,
    visitsNoReq,
    searchTitleId,
    startDate,
    endDate,
  ]);

  const handleReqDate = (d) => {
    const date = new Date(`${d}T00:00:00`);

    setReqDate(d);
    setStartDate(startOfDay(date).toISOString());
    setEndDate(endOfDay(date).toISOString());
  };
  return (
    <FormContainer>
      <ScrollUp position="right" />
      <Meta title={`Waiting Visit List | ClinicEasy`} />
      {/* Print Visit List */}
      <div style={{ display: "none" }}>
        <VisitListPrint ref={componentRef} detailedVisits={detailedVisits} />
      </div>

      {/* ======================== BREADCRUMB SECTION ======================  */}
      {loading && (
        <>
          <Skeleton height="50px" className="mb-5" />
          <Loader />
        </>
      )}

      <Map
        titles={[
          {
            idx: "1",
            label: (
              <>
                <FaHome /> Home
              </>
            ),
            path: `/`,
            active: false,
          },
          {
            idx: "2",
            label: `Clinic List Screen`,
            path: `/clinic-list-screen`,
            active: false,
          },
          {
            idx: "3",
            label: clinic ? `${clinic.clinicName}` : "...",
            path: clinic ? `/clinic-screen/${clinic._id}` : "...",
            active: false,
          },
          {
            idx: "4",
            label: `Vist List Screen`,
            path: ``,
            active: true,
          },
        ]}
      />
      <Row className="justify-content-end">
        {clinic && (
          <Col
            id="addVisitLink"
            className="align-self-center font-weight-bold text-danger"
            style={{
              cursor: "pointer",
              textAlign: "right",
            }}
            onClick={() =>
              navigate(
                `/patient-list-screen/${clinic.clinicOwnerId}?clinicId=${clinic._id}&reqDate=${reqDate}`,
              )
            }
          >
            <Button className="flex items-center gap-2">
              <FaExternalLinkAlt />
              Add Visit
            </Button>
          </Col>
        )}
      </Row>
      <Row>
        <Col>
          <h3>Waiting Visits Search</h3>
          <Row className="px-3">
            <FormGroup className="col-12 mt-2 w-100">
              <select
                className="custom-select text-bold"
                type="text"
                onChange={(e) => handleSearchTitleId(e)}
              >
                {searchOptions.map((o) => (
                  <option key={o.idx} value={o.idx}>
                    {o.title}
                  </option>
                ))}
              </select>
            </FormGroup>
          </Row>
        </Col>
      </Row>
      {searchTitleId === "0" ? (
        <Form.Group controlId="reqDate" className="col-sm-12 w-100">
          <Form.Label>
            <h2>Visit Date</h2>
          </Form.Label>
          <Form.Control
            type="date"
            value={reqDate}
            onChange={(e) => handleReqDate(e.target.value)}
            className="form-control"
          />
        </Form.Group>
      ) : (
        <Row>
          <Form.Group controlId="startDate" className="col-sm-6 w-50 ">
            <Form.Label>
              <h2>Start from</h2>
            </Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="form-control"
            />
          </Form.Group>
          <Form.Group controlId="endDate" className="col-sm-6 w-50">
            <Form.Label>
              <h2>to end of</h2>
            </Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="form-control"
            />
          </Form.Group>
        </Row>
      )}

      {clinicError && (
        <Message
          children={`Loading Clinic Details Error: ${clinicError}`}
          variant={`danger`}
        />
      )}

      {/* ============================= VISITS EXCEL  ============================= */}

      {clinicVisits?.length > 0 && (
        <Row className="my-4 justify-content-center">
          <Col>
            <CSVLink
              data={csv}
              filename={`${clinic && clinic.clinicName} at ${reqDate}`}
            >
              <FaFileExcel /> Download as Excel
            </CSVLink>
          </Col>
          <Col>
            <span
              className="text-primary mb-2"
              style={{ cursor: "pointer" }}
              onClick={() => handleBackup()}
            >
              <FaDownload /> Backup Visits Data
            </span>
          </Col>
        </Row>
      )}

      {/* ============================= VISITS BACKUP  ============================= */}
      {/* <pre>{JSON.stringify(detailedVisits, null, 2)}</pre> */}
      {errorVisit && (
        <Message
          children={`Loading Visit Backup Err: ${errorVisit}`}
          variant={`danger`}
        />
      )}

      {progression && progression <= 100 && (
        <Row className="justify-content-center my-4">
          <ProgressBar
            variant="success"
            min={0}
            max={100}
            animated
            label={`${progression.toFixed(1)} %`}
            now={progression}
            style={{ width: "75%", height: "60px" }}
          />
          <div
            className="text-center text-success mt-2"
            style={{ fontSize: "1.1rem" }}
          >
            {progression < 100 ? (
              <>
                Processing… this may take a moment. <br /> Please do not close
                or navigate away.
              </>
            ) : (
              <>
                Processing completed successfully. <br />
                You can now download or print the visits list.{" "}
                <FaArrowAltCircleDown className="mb-2" />
                <FaArrowAltCircleDown className="mb-2" />
              </>
            )}
          </div>
        </Row>
      )}
      {progression === 100 && (
        <ReactToPrint
          trigger={() => (
            <Row className="m-3 text-center border border-2 border-success rounded p-3">
              <Col>
                <span
                  className="text-success mb-2 "
                  style={{ cursor: "pointer" }}
                >
                  <FaPrint /> Download / Print Visits List
                </span>
              </Col>
            </Row>
          )}
          content={() => componentRef.current}
          documentTitle={``}
        />
      )}
      {/* ============================= DISPLAY VISITS ============================= */}

      {/* <pre>{JSON.stringify(clinicVisits, null, 2)}</pre> */}
      {loadingVisits && <Skeleton height="50px" className="mb-2" count={3} />}

      {errorVisits && (
        <Message
          children={`Loading Clinic Visits Err: ${errorVisits}`}
          variant={`danger`}
        />
      )}

      {clinicVisits?.length === 0 && (
        <Message children={"No Arranged visits yet"} variant={`info`} />
      )}

      <ListGroup>
        {clinicVisits &&
          clinicVisits.map((v, order = 0) => {
            return (
              <ListGroupItem
                style={{
                  backgroundColor: v.isClosed ? "#9FA6B2" : "#00abff",
                }}
                className="p-3 my-2 text-white font-weight-bold"
                onClick={() =>
                  navigate(
                    `/patient-screen/${v.patientDetails._id}?clinicId=${clinicId}`,
                  )
                }
                key={v._id}
              >
                <Row className="flex-row-reverse text-center align-items-center g-2">
                  <Col xs={2} sm={2} md={1}>
                    {"   "}
                    <FaExternalLinkAlt />{" "}
                  </Col>

                  <Col xs={12} sm={5} md={5} className="text-right">
                    {`${v.patientDetails.firstName} ${v.patientDetails.secondName} ${v.patientDetails.thirdName} ${v.patientDetails.fourthName}`}
                  </Col>

                  <Col xs={12} sm={3} md={4} className="text-center">
                    {`${format(
                      parseISO(v.visitDate),
                      "dd-MMM-yyyy | hh:mm a",
                    )}`}
                  </Col>

                  <Col xs={12} sm={2} md={2}>
                    {`Paid: ${v.paid}`}
                  </Col>
                </Row>
                <Row className="mb-2 justify-content-center">
                  <Col
                    style={{
                      display: "flex",
                      gap: "8px",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {v.visitServices?.map((t) => (
                      <span key={t._id} className="hashtag">
                        #{t.serviceLabel}
                      </span>
                    ))}
                  </Col>
                </Row>
                <Col className="col-sm-1 d-flex align-self-start ">
                  <span className="badge bg-white text-primary rounded-pill text-white">
                    {order + 1}
                  </span>
                </Col>
              </ListGroupItem>
            );
          })}
      </ListGroup>

      {/* ============================ SUMMARY ============================ */}

      <p className="font-weight-bold text-success ">
        {`Total Paid: ${
          clinicVisits ? clinicVisits.reduce((sum, v) => sum + v.paid, 0) : 0
        } |     
        Visit Count: ${clinicVisits ? clinicVisits.length : 0}`}
      </p>

      {/* ============================ ADD NEW VISIT BOTTOM ============================ */}
      {!loading && (
        <Button
          className="btn btn-secondary btn-block p-1"
          onClick={() =>
            navigate(
              `/patient-list-screen/${clinic.clinicOwnerId}?clinicId=${clinic._id}&reqDate=${reqDate}`,
            )
          }
        >
          <h3>{`Click Here to Add New Visit in ${reqDate}`} </h3>
        </Button>
      )}
      {/* <Help /> */}
    </FormContainer>
  );
};

export default VisitListScreen;
